import React, { useRef, useState } from "react";
import { ImageIcon, Loader2, Trash2, Upload, ArrowLeft, ArrowRight } from "lucide-react";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { useAuth } from "../../context/AuthContext";
import { useT } from "../../context/LanguageContext";
import api from "../../lib/api";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_BYTES = 8 * 1024 * 1024;
const ACCEPTED = ["image/jpeg", "image/png", "image/webp"];
const GALLERY_MAX = 6;

// Logo + work-gallery manager for /booking-page-settings. Uploads apply
// instantly (independent of the text-fields save form below).
export const BookingPagePhotos = () => {
  const { user, setUser } = useAuth();
  const { t } = useT();
  const logoInputRef = useRef(null);
  const galleryInputRef = useRef(null);
  const [logoBusy, setLogoBusy] = useState(false);
  const [galleryBusy, setGalleryBusy] = useState(false);

  const logoUrl = user?.booking_logo_url ? `${BACKEND_URL}${user.booking_logo_url}` : null;
  const gallery = Array.isArray(user?.booking_gallery_images) ? user.booking_gallery_images : [];

  const syncUser = (data) => {
    setUser(data);
    try { localStorage.setItem("tasklio_user", JSON.stringify(data)); } catch { /* server is truth */ }
  };

  const validateFile = (file) => {
    if (!file) return false;
    if (!ACCEPTED.includes(file.type)) {
      toast.error(t("bps.invalidImage"));
      return false;
    }
    if (file.size > MAX_BYTES) {
      toast.error(t("bps.imageTooLarge"));
      return false;
    }
    return true;
  };

  const uploadFile = async (file, endpoint, setBusy, successKey) => {
    if (!validateFile(file)) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(endpoint, fd);
      syncUser(data);
      toast.success(t(successKey));
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : t("bps.uploadError"));
    } finally {
      setBusy(false);
    }
  };

  const removeLogo = async () => {
    setLogoBusy(true);
    try {
      const { data } = await api.delete("/booking-page/logo");
      syncUser(data);
      toast.success(t("bps.logoRemoved"));
    } catch {
      toast.error(t("bps.uploadError"));
    } finally {
      setLogoBusy(false);
    }
  };

  const deletePhoto = async (imageId) => {
    setGalleryBusy(true);
    try {
      const { data } = await api.delete(`/booking-page/gallery/${imageId}`);
      syncUser(data);
      toast.success(t("bps.photoRemoved"));
    } catch {
      toast.error(t("bps.uploadError"));
    } finally {
      setGalleryBusy(false);
    }
  };

  const movePhoto = async (index, delta) => {
    const target = index + delta;
    if (target < 0 || target >= gallery.length) return;
    const ids = gallery.map((g) => g.id);
    [ids[index], ids[target]] = [ids[target], ids[index]];
    setGalleryBusy(true);
    try {
      const { data } = await api.patch("/booking-page/gallery/order", { image_ids: ids });
      syncUser(data);
    } catch {
      toast.error(t("bps.uploadError"));
    } finally {
      setGalleryBusy(false);
    }
  };

  return (
    <div className="space-y-8">
      {/* Logo */}
      <div>
        <Label>{t("bps.logo")}</Label>
        <p className="mt-0.5 text-xs text-gray-500">{t("bps.logoHelper")}</p>
        <div className="mt-3 flex items-center gap-4">
          {logoUrl && (
            <img
              src={logoUrl}
              alt="Logo"
              className="w-20 h-20 rounded-xl border border-gray-200 bg-white object-contain p-1.5"
              data-testid="bps-logo-preview"
            />
          )}
          <div className="flex flex-wrap gap-2">
            <input
              ref={logoInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                e.target.value = "";
                if (f) uploadFile(f, "/booking-page/logo", setLogoBusy, "bps.logoUpdated");
              }}
              data-testid="bps-logo-file-input"
            />
            <Button
              type="button"
              variant="outline"
              disabled={logoBusy}
              onClick={() => logoInputRef.current?.click()}
              className="h-10 px-4 font-semibold border-gray-300 bg-white text-gray-900 hover:bg-gray-50"
              data-testid="bps-logo-upload-button"
            >
              {logoBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                <><Upload className="w-4 h-4 mr-1.5" /> {logoUrl ? t("bps.replaceLogo") : t("bps.uploadLogo")}</>
              )}
            </Button>
            {logoUrl && (
              <Button
                type="button"
                variant="outline"
                disabled={logoBusy}
                onClick={removeLogo}
                className="h-10 px-4 font-semibold border-gray-300 bg-white text-red-600 hover:bg-red-50"
                data-testid="bps-logo-remove-button"
              >
                <Trash2 className="w-4 h-4 mr-1.5" /> {t("bps.removeLogo")}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Gallery */}
      <div>
        <div className="flex items-center justify-between gap-3">
          <div>
            <Label>{t("bps.gallery")}</Label>
            <p className="mt-0.5 text-xs text-gray-500">{t("bps.galleryHelper")}</p>
            <p className="text-xs text-gray-500">{t("bps.galleryTip")}</p>
          </div>
          <span className="text-sm font-medium text-gray-600 tabular-nums shrink-0" data-testid="bps-gallery-count">
            {t("bps.galleryCount", gallery.length)}
          </span>
        </div>

        {gallery.length > 0 && (
          <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="bps-gallery-grid">
            {gallery.map((img, i) => (
              <div key={img.id} className="group" data-testid={`bps-gallery-item-${img.id}`}>
                <div className="rounded-xl overflow-hidden bg-gray-100 aspect-[4/3] border border-gray-200">
                  <img
                    src={`${BACKEND_URL}${img.url}`}
                    alt=""
                    loading="lazy"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="mt-1.5 flex items-center justify-center gap-1">
                  <button
                    type="button"
                    disabled={galleryBusy || i === 0}
                    onClick={() => movePhoto(i, -1)}
                    aria-label={t("bps.moveLeft")}
                    title={t("bps.moveLeft")}
                    className="p-1.5 rounded-md border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-30"
                    data-testid={`bps-gallery-move-left-${img.id}`}
                  >
                    <ArrowLeft className="w-3.5 h-3.5" />
                  </button>
                  <button
                    type="button"
                    disabled={galleryBusy || i === gallery.length - 1}
                    onClick={() => movePhoto(i, 1)}
                    aria-label={t("bps.moveRight")}
                    title={t("bps.moveRight")}
                    className="p-1.5 rounded-md border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-30"
                    data-testid={`bps-gallery-move-right-${img.id}`}
                  >
                    <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                  <button
                    type="button"
                    disabled={galleryBusy}
                    onClick={() => deletePhoto(img.id)}
                    aria-label={t("bps.deletePhoto")}
                    title={t("bps.deletePhoto")}
                    className="p-1.5 rounded-md border border-gray-200 bg-white text-red-600 hover:bg-red-50 disabled:opacity-30"
                    data-testid={`bps-gallery-delete-${img.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        <input
          ref={galleryInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            e.target.value = "";
            if (f) uploadFile(f, "/booking-page/gallery", setGalleryBusy, "bps.photoUploaded");
          }}
          data-testid="bps-gallery-file-input"
        />
        <div className="mt-3">
          {gallery.length >= GALLERY_MAX ? (
            <p className="text-sm text-gray-500" data-testid="bps-gallery-full-message">
              {t("bps.galleryFull")}
            </p>
          ) : (
            <Button
              type="button"
              variant="outline"
              disabled={galleryBusy}
              onClick={() => galleryInputRef.current?.click()}
              className="h-10 px-4 font-semibold border-gray-300 bg-white text-gray-900 hover:bg-gray-50"
              data-testid="bps-gallery-add-button"
            >
              {galleryBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                <><ImageIcon className="w-4 h-4 mr-1.5" /> {t("bps.addPhoto")}</>
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
